// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import {
    DisasterRecoveryEntityStatus as EntityResponseStatus,
    IDisasterRecoveryEntityResponse,
    IDisasterRecoveryResponse,
} from 'common/api/resources/DisasterRecovery';
import { Loader } from 'common/components';
import {
    Action,
    List,
    Text,
    Translate,
} from '@plesk/ui-library';
import { getActionColumnProps } from 'common/helpers/list';
import { Button } from 'admin/common/components/Button/Button';
import { pathTo } from 'common/helpers/core';
import { DISASTER_RECOVERY_HISTORY } from 'admin/disasterRecovery/constants/test';
import { DisasterRecoveryEntityStatus } from 'admin/disasterRecovery/components/HistoryTable/DisasterRecoveryEntityStatus';
import { formatTableDate } from 'common/date';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { COMPUTE_RESOURCE_STATUS } from 'admin/computeResource/constants';
import { EmptyView } from 'admin/disasterRecovery/components/HistoryTable/Styles';
// import { ITaskResponse } from 'common/api/resources/Task';
import { dataCySelector } from 'common/tests/selectors';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import OriginalData from 'admin/disasterRecovery/components/HistoryTable/DisasterRecoveryOriginalData';
import { Dialog } from 'common/components/Dialog/Dialog';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as historyActions from 'common/modules/disasterRecovery/actions';
import InfiniteScroll from 'common/components/InfinityScroll/InfinityScroll';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { RootState } from 'admin/core/store';
import { Link } from 'react-router-dom';

interface IDisasterRecoveryProcessRowProps extends RouteComponentProps {
    process: IDisasterRecoveryResponse;
    isLoading: boolean;
    isProcessSelected: boolean;
    handleTaskModalOpen: (entity: IDisasterRecoveryEntityResponse) => void;
    handleAddEntitiesToSelection: (process: IDisasterRecoveryResponse, entities: IDisasterRecoveryEntityResponse[]) => void;
    handleRemoveEntitiesFromSelection: (process: IDisasterRecoveryResponse, entities: IDisasterRecoveryEntityResponse[]) => void;
    handleDeleteOriginalData: (entity: IDisasterRecoveryEntityResponse) => void;
}

export type DisasterRecoveryProcessRowProps =
    IDisasterRecoveryProcessRowProps &
    ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

const columns = [{
    key: 'colVmId',
    width: '1%',
    title: <Translate content="disasterRecovery.history.entities.list.vmId" />,
}, {
    key: 'colVmName',
    title: <Translate content="disasterRecovery.history.entities.list.vmName" />,
}, {
    key: 'colStatus',
    title: <Translate content="disasterRecovery.history.entities.list.status" />,
}, {
    key: 'colStartedAt',
    title: <Translate content="disasterRecovery.history.entities.list.startedAt" />,
}, {
    key: 'colFinishedAt',
    title: <Translate content="disasterRecovery.history.entities.list.finishedAt" />,
}, {
    key: 'colDestinationCr',
    title: <Translate content="disasterRecovery.history.entities.list.destinationCr" />,
}, getActionColumnProps({ width: '5%' }),
];

export const DisasterRecoveryProcessRow: React.FC<DisasterRecoveryProcessRowProps> = ({
    process,
    isLoading,
    isProcessSelected,
    handleTaskModalOpen,
    handleAddEntitiesToSelection,
    handleRemoveEntitiesFromSelection,
    handleDeleteOriginalData,
    disasterRecoveryHistoryActions: {
        deleteOriginalData,
        getDisasterRecoveryEntitiesForProcess,
        loadMoreDisasterRecoveryEntitiesOnScroll,
    },
}) => {
    const [selection, setSelection] = React.useState<string[]>([]);
    const [isOriginalDataDialogOpen, setOriginalDataDialogOpen] = React.useState<boolean>(false);
    const [viewOriginalDataEntityId, setViewOriginalDataEntityId] = React.useState<number>(0);

    React.useEffect(() => {
        if (!process.entities) {
            getDisasterRecoveryEntitiesForProcess(process.id);
        }
    }, [process]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const entities = (process.entities?.data ?? []);
        if (entities.length > 0) {
            // process was deselected
            if (!isProcessSelected) {
                // deselect all entities if all entities were selected
                if (entities.every((entity) => selection.includes(entity.id.toString()))) {
                    handleRemoveEntitiesFromSelection(process, entities);
                    setSelection([]);
                }
            } else {
                handleAddEntitiesToSelection(process, entities);
                setSelection(entities.map((entity) => entity.id.toString()));
            }
        }

    }, [isProcessSelected, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleEntitySelect = (ids: string[]) => {
        const entities = (process.entities?.data ?? []);
        if (ids.length >= selection.length) {
            const newlyAddedEntityIds = ids.filter((id: string) => !selection.includes(id));
            handleAddEntitiesToSelection(
                process,
                entities.filter(entity => newlyAddedEntityIds.includes(entity.id.toString()))
            );
        } else {
            const removedEntityIds = selection.filter((id: string) => !ids.includes(id));
            handleRemoveEntitiesFromSelection(
                process,
                entities.filter(entity => removedEntityIds.includes(entity.id.toString()))
            );
        }

        setSelection(ids);
    };

    const handleDeletedManually = async (entityIds: number[]) => {
        await deleteOriginalData({
            disaster_recovery_ids: [],
            disaster_recovery_entity_ids: entityIds,
            is_deleted_manually: true,
        });

        setOriginalDataDialogOpen(false);
    };

    const list = (process.entities?.data ?? []).map((entity: IDisasterRecoveryEntityResponse) => {
        let deleteOriginalDataTooltip = (
            <Translate content={'disasterRecovery.history.entities.list.deleteOriginalData.tooltip.alreadyDeleted'} />
        );

        if (process.source_compute_resource.status !== COMPUTE_RESOURCE_STATUS.ACTIVE) {
            deleteOriginalDataTooltip =
                (<Translate content={'disasterRecovery.history.entities.list.deleteOriginalData.tooltip.crNotActive'} />);
        } else if (entity.is_migrated_back) {
            deleteOriginalDataTooltip =
                (<Translate content={'disasterRecovery.history.entities.list.deleteOriginalData.tooltip.restored'} />);
        } else if (entity.status !== EntityResponseStatus.COMPLETED) {
            deleteOriginalDataTooltip =
                (<Translate content={'disasterRecovery.history.entities.list.deleteOriginalData.tooltip.failed'} />);
        } else if (!!entity.compute_resource_vm.deleted_at) {
            deleteOriginalDataTooltip =
                (<Translate content={'disasterRecovery.history.entities.list.deleteOriginalData.tooltip.vmDeleted'} />);
        }

        const actionEl = (
            <>
                <Button
                    onClick={() => handleViewOriginalData(entity.id)}
                    fill={false}
                    ghost={true}
                    icon="box-diagonal-top-in"
                    data-cy={dataCySelector(entity.id, DISASTER_RECOVERY_HISTORY.VIEW_ORIGINAL_DATA_FOR_ENTITY)}
                    tooltip={<Translate content="disasterRecovery.history.actions.viewOriginalData.button" />}
                />
                <Button
                    intent={INTENT_TYPE.PRIMARY}
                    className="action-icon"
                    fill={false}
                    color="#fff"
                    data-cy={dataCySelector(entity.id, DISASTER_RECOVERY_HISTORY.DELETE_ORIGINAL_DATA_FOR_ENTITY)}
                    disabled={entity.is_original_data_deleted
                        || entity.disaster_recovery.source_compute_resource.status !== COMPUTE_RESOURCE_STATUS.ACTIVE
                        || entity.is_migrated_back
                        || entity.status !== EntityResponseStatus.COMPLETED
                        || !!entity.compute_resource_vm.deleted_at
                    }
                    tooltip={deleteOriginalDataTooltip}
                    onClick={() => handleDeleteOriginalData(entity)}
                    isLoading={entity.is_deleting_original_data}
                >
                    <Translate content={'disasterRecovery.history.entities.list.deleteOriginalData.button'} />
                </Button>
            </>
        );

        return {
            colVmId: entity.compute_resource_vm.id,
            colVmName: (entity.compute_resource_vm.deleted_at
                ? (<>
                    <Text bold>{entity.compute_resource_vm.name}</Text>
                    <br />
                    <Translate
                        content="disasterRecovery.history.entities.list.serverDeletedAt"
                        params={{ date: formatTableDate(entity.compute_resource_vm.deleted_at) }}
                    />
                </>)
                : <Action
                    component={Link}
                    to={pathTo(`servers/${entity.compute_resource_vm.id}`)}
                >
                    {entity.compute_resource_vm.name}
                </Action>
            ),
            colStatus: (
                <DisasterRecoveryEntityStatus
                    status={entity.status}
                    compact={true}
                    entity={entity}
                    onTaskModalOpen={() => handleTaskModalOpen(entity)}
                />
            ),
            colStartedAt: entity.started_at ? formatTableDate(entity.started_at) : null,
            colFinishedAt: entity.finished_at ? formatTableDate(entity.finished_at) : null,
            colDestinationCr: (
                entity.target_compute_resource
                    ? (<Action
                        component={Link}
                        to={pathTo(`compute_resources/${entity.target_compute_resource.id}`)}
                    >
                        {entity.target_compute_resource.name}
                    </Action>)
                    : <>&mdash;</>
            ),
            colActions: actionEl,
            key: entity.id.toString(),
        };
    });

    const handleOriginalDataDialogClose = () => {
        setOriginalDataDialogOpen(false);
    };

    const handleViewOriginalData = (entityId: number) => {
        setViewOriginalDataEntityId(entityId);
        setOriginalDataDialogOpen(true);
    };

    const loadMoreEntities = () => loadMoreDisasterRecoveryEntitiesOnScroll(process);

    return (
        <InfiniteScroll
            loadMore={loadMoreEntities}
            hasMore={!!process.entities?.links.next}
        >
            <Loader isLoading={isLoading} center={false}>
                <List
                    columns={columns}
                    data={list}
                    selection={selection}
                    onSelectionChange={handleEntitySelect}
                    emptyView={
                        <EmptyView>
                            <Translate content="disasterRecovery.history.entities.list.empty" />
                        </EmptyView>
                    }
                />
                <Dialog
                    heading={<Translate content="disasterRecovery.history.originalDataDialog.title"/>}
                    closeHandler={handleOriginalDataDialogClose}
                    isOpen={isOriginalDataDialogOpen}
                    size={SIZE.MD}
                >
                    <OriginalData
                        entityIds={[viewOriginalDataEntityId]}
                        processIds={[]}
                        handleDeletedManually={handleDeletedManually}
                    />
                </Dialog>
            </Loader>
        </InfiniteScroll>
    );
};

const mapStateToProps = (state: RootState) => ({
    isLoadingInfinityScroll: state.app.loadingFlags.has(LOADING_FLAGS.DISASTER_RECOVERY_ENTITIES_INFINITY_SCROLL),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    disasterRecoveryHistoryActions: bindActionCreators(historyActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DisasterRecoveryProcessRow));
